import React from 'react';
import { LottiePlayer } from '@shared/components/LottiePlayer';
import { useHistory, useTranslation } from '@shared/core';
import type { TranslateFunction } from '@locales/generated';
import { Box, Flex, LinkV2, TextV2 } from '@withjoy/joykit';
import { useRoutePaths } from '@apps/registry/admin/Registry.routes';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { ScrollToTop } from '@apps/common/components/ScrollToTop/ScrollToTop';
import { useQuickAddController } from '@apps/registry/admin/routes/QuickAdd/QuickAdd.controller';
import {
  StyledWrapper,
  StyledPageBox,
  StyledStepColumn,
  StyledLottieWrapper,
  StyledAddButton,
  StyledAddIcon,
  StyledVideoWrapper,
  StyledVideo,
  StyledFooterColumn,
  StyledLinkV2,
  styles,
} from './BrowserButtonSection.styles';

type BrowserButtonSectionProps = Readonly<{
  eventId: string;
}>;

type HeaderProps = Readonly<{
  title: TranslateFunction;
  subtitle: string;
  addButton: TranslateFunction;
  bookmarkletScript: `javascript:(${string})(${string});`;
  handleClick: (event: React.SyntheticEvent) => void;
  handleDragStart: (event: React.DragEvent<HTMLAnchorElement>) => void;
  handleDrop: (event: React.DragEvent<HTMLAnchorElement>) => void;
}>;

const LOTTIE_ARROW_URL = 'https://withjoy.com/assets/public/apps/registry/lottie/quick-add/arrow/data.json';

const Header: React.FC<HeaderProps> = ({ title, subtitle, addButton, bookmarkletScript, handleClick, handleDragStart, handleDrop }) => {
  return (
    <>
      <TextV2
        typographyVariant="display3"
        marginTop={32}
        color="mono14"
      >
        {title()}
      </TextV2>
      <TextV2
        typographyVariant="body3"
        color="mono14"
        marginTop={40}
        marginBottom={24}
        textAlign="center"
        maxWidth={622}
      >
        {subtitle}
      </TextV2>
      <Box position="relative">
        <StyledLottieWrapper __css={styles.lottieArrow}>
          <LottiePlayer
            autoplay
            loop
            lottieUrl={LOTTIE_ARROW_URL}
          />
        </StyledLottieWrapper>
        <StyledAddButton
          href={bookmarkletScript}
          onClick={handleClick}
          onDragStart={handleDragStart}
          onDragEnd={handleDrop}
          {...styles.addButton}
        >
          <StyledAddIcon style={styles.icon} />
          {addButton()}
        </StyledAddButton>
      </Box>
    </>
  );
};

const BrowserButtonSection: React.FC<BrowserButtonSectionProps> = ({ eventId }) => {
  const { browser, bookmarkletScript, config, handleClick, handleDragStart, handleDrop, switchBrowser } = useQuickAddController({ eventId });

  const { t } = useTranslation('yourRegistry');
  const { title, addButton, showMeLink } = t('quickAdd', 'emptyCatalogState');
  const history = useHistory();
  const routes = useRoutePaths();
  // TODO: Baby Registry Launch -- add telemetry
  const handleOnShowMeClick = useEventCallback(() => {
    history.push(routes.quickAdd.path);
  }, [history, routes]);

  const subtitle = t(
    'quickAdd',
    'emptyCatalogState',
    'subtitle',
  )({
    lineBreak: () => <br />,
  });

  const stepSafari = t(
    'quickAdd',
    'stepSafari',
  )({
    chromeLink: () => (
      <LinkV2
        typographyVariant="hed3"
        textDecoration="none"
        onClick={() => switchBrowser('Chrome')}
      >
        Chrome
      </LinkV2>
    ),
    firefoxLink: () => (
      <LinkV2
        typographyVariant="hed3"
        textDecoration="none"
        onClick={() => switchBrowser('Firefox')}
      >
        Firefox
      </LinkV2>
    ),
  });

  if (browser === 'Safari') {
    return (
      <StyledWrapper __css={styles.wrapper}>
        <ScrollToTop />
        <StyledPageBox
          spacing={9}
          paddingX={[0, 6, 6, 0]}
          __css={styles.pageBox}
        >
          <Flex
            flexDirection="column"
            alignItems="center"
          >
            <TextV2
              typographyVariant="hed3"
              textAlign="center"
              maxWidth={530}
              marginTop={40}
            >
              {stepSafari}
            </TextV2>
            <StyledVideoWrapper __css={styles.safariVideoWrapper}>
              <StyledVideo
                playsInline
                autoPlay
                muted
                loop
                style={styles.video}
              >
                <source src={config?.registryQuickAddVideo3} />
              </StyledVideo>
            </StyledVideoWrapper>
          </Flex>
        </StyledPageBox>
      </StyledWrapper>
    );
  }
  return (
    <StyledWrapper __css={styles.wrapper}>
      <ScrollToTop />
      <StyledPageBox
        spacing={9}
        paddingX={[0, 6, 6, 0]}
        __css={styles.pageBox}
      >
        <Flex
          flexDirection="column"
          alignItems="center"
        >
          <Header
            title={title}
            subtitle={subtitle}
            addButton={addButton}
            bookmarkletScript={bookmarkletScript}
            handleClick={handleClick}
            handleDragStart={handleDragStart}
            handleDrop={handleDrop}
          />
        </Flex>
        <StyledStepColumn __css={styles.stepColumn}>
          <StyledVideoWrapper __css={styles.videoWrapper}>
            <StyledVideo
              playsInline
              autoPlay
              muted
              loop
              style={styles.video}
            >
              <source src={config?.registryQuickAddVideo2} />
            </StyledVideo>
          </StyledVideoWrapper>
        </StyledStepColumn>
        <StyledFooterColumn __css={styles.footerColumn}>
          <StyledLinkV2
            typographyVariant="button2"
            textDecoration="underline"
            onClick={handleOnShowMeClick}
            textAlign="center"
            color="mono14"
          >
            {showMeLink()}
          </StyledLinkV2>
        </StyledFooterColumn>
      </StyledPageBox>
    </StyledWrapper>
  );
};

export { BrowserButtonSection };
